/* ═══════════════════════════════════════════════
   RUGS GALLERY — Theme JavaScript
   ═══════════════════════════════════════════════ */

document.addEventListener('DOMContentLoaded', () => {
  const routesRoot =
    window.Shopify?.routes?.root ||
    '/';

  const cartRoutes = {
    get: `${routesRoot}cart.js`,
    add: `${routesRoot}cart/add.js`,
    change: `${routesRoot}cart/change.js`,
  };

  /* ── Sticky Header ─────────────────────────── */

  const header = document.getElementById('siteHeader');

  if (header) {
    const onScroll = () => {
      header.classList.toggle('scrolled', window.scrollY > 60);
    };

    window.addEventListener('scroll', onScroll, {
      passive: true,
    });

    onScroll();
  }

  /* ── Toast Notification ────────────────────── */

  window.showToast = (message, duration = 3000) => {
    let toast = document.getElementById('globalToast');

    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'globalToast';
      toast.className = 'toast';
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('show');

    clearTimeout(toast._timer);

    toast._timer = setTimeout(() => {
      toast.classList.remove('show');
    }, duration);
  };

  /* ── Helpers ───────────────────────────────── */

  const requestJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      throw new Error(
        data?.description ||
        data?.message ||
        'Shopify cart request failed'
      );
    }

    return data;
  };

  const escapeHTML = (value = '') => {
    const div = document.createElement('div');
    div.textContent = String(value);
    return div.innerHTML;
  };

  const formatMoney = (cents) => {
    const locale = window.RGTheme?.isRTL
      ? 'ar-EG'
      : 'en-EG';

    return (Number(cents) / 100).toLocaleString(locale, {
      style: 'currency',
      currency: 'EGP',
      maximumFractionDigits: 0,
    });
  };

  const setCartCount = (count) => {
    const cartCount = document.getElementById('cartCount');

    if (cartCount) {
      cartCount.textContent = count;
    }
  };

  /* ── Cart Count ────────────────────────────── */

  window.updateCartCount = async () => {
    try {
      const cart = await requestJSON(cartRoutes.get);
      setCartCount(cart.item_count);
    } catch (error) {
      console.error('Unable to update cart count:', error);
    }
  };

  /* ── Cart Drawer Elements ──────────────────── */

  let cartDrawer = null;
  let cartOverlay = null;
  let cartItems = null;
  let cartFooter = null;

  const getCartElements = () => {
    cartDrawer = document.getElementById('cartDrawer');
    cartOverlay = document.getElementById('cartOverlay');
    cartItems = document.getElementById('cartItems');
    cartFooter = document.getElementById('cartFooter');
  };

  /* ── Build Drawer Only If Missing ──────────── */

  const buildCartDrawer = () => {
    if (!document.getElementById('cartOverlay')) {
      const overlay = document.createElement('div');

      overlay.id = 'cartOverlay';
      overlay.className = 'cart-drawer-overlay';

      document.body.appendChild(overlay);
    }

    if (!document.getElementById('cartDrawer')) {
      const drawer = document.createElement('div');

      drawer.id = 'cartDrawer';
      drawer.className = 'cart-drawer';

      drawer.innerHTML = `
        <div class="cart-drawer-header">
          <h2 class="cart-drawer-title">
            ${window.RGTheme?.isRTL ? 'السلة' : 'Cart'}
          </h2>

          <button
            type="button"
            class="cart-drawer-close"
            id="cartClose"
            aria-label="Close cart"
          >
            ✕
          </button>
        </div>

        <div class="cart-drawer-items" id="cartItems">
          <p style="
            color:var(--muted);
            text-align:center;
            padding:40px 0;
          ">
            ${
              window.RGTheme?.isRTL
                ? 'السلة فارغة'
                : 'Your cart is empty'
            }
          </p>
        </div>

        <div
          class="cart-drawer-footer"
          id="cartFooter"
          style="display:none;"
        >
          <div class="cart-total">
            <span class="cart-total-label">
              ${window.RGTheme?.isRTL ? 'الإجمالي' : 'Total'}
            </span>

            <span
              class="cart-total-price"
              id="cartTotalPrice"
            ></span>
          </div>

          <a
            href="/checkout"
            class="cart-checkout-btn"
            style="
              display:block;
              text-align:center;
              text-decoration:none;
            "
          >
            ${
              window.RGTheme?.isRTL
                ? 'إتمام الشراء ←'
                : 'Checkout →'
            }
          </a>
        </div>
      `;

      document.body.appendChild(drawer);
    }

    getCartElements();
  };

  /* ── Render Cart Items ─────────────────────── */

  const renderCartItems = (cart) => {
    getCartElements();

    if (!cartItems) {
      return;
    }

    setCartCount(cart.item_count);

    if (cart.item_count === 0) {
      cartItems.innerHTML = `
        <p style="
          color:var(--muted);
          text-align:center;
          padding:40px 0;
        ">
          ${
            window.RGTheme?.isRTL
              ? 'السلة فارغة'
              : 'Your cart is empty'
          }
        </p>
      `;

      if (cartFooter) {
        cartFooter.style.display = 'none';
      }

      return;
    }

    cartItems.innerHTML = cart.items
      .map((item, index) => {
        const line = index + 1;
        const productTitle = escapeHTML(item.product_title);
        const variantTitle = escapeHTML(item.variant_title || '');
        const image = item.image
          ? escapeHTML(item.image)
          : '';

        return `
          <div class="cart-item">

            ${
              image
                ? `
                  <a href="${escapeHTML(item.url || '#')}">
                    <img
                      class="cart-item-img"
                      src="${image}"
                      alt="${productTitle}"
                      loading="lazy"
                    >
                  </a>
                `
                : `
                  <div
                    class="cart-item-img"
                    style="background:var(--ivory);"
                  ></div>
                `
            }

            <div style="flex:1;min-width:0;">

              <div class="cart-item-name">
                <a
                  href="${escapeHTML(item.url || '#')}"
                  style="color:inherit;text-decoration:none;"
                >
                  ${productTitle}
                </a>
              </div>

              ${
                variantTitle &&
                variantTitle !== 'Default Title'
                  ? `
                    <div style="
                      font-size:12px;
                      color:var(--muted);
                      margin-bottom:4px;
                    ">
                      ${variantTitle}
                    </div>
                  `
                  : ''
              }

              <div class="cart-item-price">
                ${formatMoney(item.final_price)}
              </div>

              <div class="cart-item-qty">

                <button
                  type="button"
                  class="qty-btn"
                  data-cart-action="quantity"
                  data-line="${line}"
                  data-quantity="${Math.max(0, item.quantity - 1)}"
                  aria-label="Decrease quantity"
                >
                  −
                </button>

                <span class="qty-num">
                  ${item.quantity}
                </span>

                <button
                  type="button"
                  class="qty-btn"
                  data-cart-action="quantity"
                  data-line="${line}"
                  data-quantity="${item.quantity + 1}"
                  aria-label="Increase quantity"
                >
                  +
                </button>

              </div>
            </div>

            <button
              type="button"
              class="cart-item-remove"
              data-cart-action="remove"
              data-line="${line}"
              data-quantity="0"
              aria-label="Remove ${productTitle}"
              style="
                border:0;
                background:none;
                padding:4px;
                cursor:pointer;
                color:var(--muted);
                font-size:15px;
                align-self:flex-start;
              "
            >
              ✕
            </button>

          </div>
        `;
      })
      .join('');

    const totalPrice =
      document.getElementById('cartTotalPrice');

    if (totalPrice) {
      totalPrice.textContent = formatMoney(cart.total_price);
    }

    if (cartFooter) {
      cartFooter.style.display = 'block';
    }
  };

  /* ── Load Cart ─────────────────────────────── */

  const loadCart = async () => {
    const cart = await requestJSON(cartRoutes.get);
    renderCartItems(cart);
    return cart;
  };

  /* ── Open / Close Cart Drawer ──────────────── */

  window.openCartDrawer = async () => {
    buildCartDrawer();

    if (!cartDrawer || !cartOverlay) {
      return;
    }

    cartDrawer.classList.add('open');
    cartOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';

    try {
      await loadCart();
    } catch (error) {
      console.error('Unable to load cart:', error);
      showToast(
        window.RGTheme?.isRTL
          ? 'تعذر تحميل السلة'
          : 'Unable to load cart'
      );
    }
  };

  window.closeCartDrawer = () => {
    getCartElements();

    if (cartDrawer) {
      cartDrawer.classList.remove('open');
    }

    if (cartOverlay) {
      cartOverlay.classList.remove('open');
    }

    document.body.style.overflow = '';
  };

  /* ── Update Cart Quantity ──────────────────── */

  const updateCartLine = async (line, quantity, button = null) => {
    if (!line || quantity < 0) {
      return;
    }

    if (button) {
      button.disabled = true;
    }

    try {
      const cart = await requestJSON(cartRoutes.change, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
        }),
      });

      renderCartItems(cart);
    } catch (error) {
      console.error('Unable to update cart:', error);

      showToast(
        window.RGTheme?.isRTL
          ? 'حدث خطأ أثناء تحديث السلة'
          : 'Unable to update cart'
      );

      try {
        await loadCart();
      } catch (loadError) {
        console.error(loadError);
      }
    } finally {
      if (button && button.isConnected) {
        button.disabled = false;
      }
    }
  };

  /* Compatibility with any older inline buttons */

  window.updateQty = (line, quantity) => {
    return updateCartLine(line, quantity);
  };

  /* ── Cart Drawer Events ────────────────────── */

  const bindCartDrawerEvents = () => {
    buildCartDrawer();

    const closeButton =
      document.getElementById('cartClose');

    if (
      closeButton &&
      closeButton.dataset.cartBound !== 'true'
    ) {
      closeButton.addEventListener('click', () => {
        closeCartDrawer();
      });

      closeButton.dataset.cartBound = 'true';
    }

    if (
      cartOverlay &&
      cartOverlay.dataset.cartBound !== 'true'
    ) {
      cartOverlay.addEventListener('click', () => {
        closeCartDrawer();
      });

      cartOverlay.dataset.cartBound = 'true';
    }

    if (
      cartItems &&
      cartItems.dataset.cartBound !== 'true'
    ) {
      cartItems.addEventListener('click', (event) => {
        const button = event.target.closest(
          '[data-cart-action]'
        );

        if (!button) {
          return;
        }

        event.preventDefault();

        const line = Number(button.dataset.line);
        const quantity = Number(button.dataset.quantity);

        updateCartLine(line, quantity, button);
      });

      cartItems.dataset.cartBound = 'true';
    }
  };

  bindCartDrawerEvents();

  /* ── AJAX Add to Cart ──────────────────────── */

  window.addToCart = async (variantId, button) => {
    if (!variantId) {
      return;
    }

    const originalContent = button
      ? button.innerHTML
      : '';

    if (button) {
      button.disabled = true;
      button.textContent = window.RGTheme?.isRTL
        ? 'جاري الإضافة…'
        : 'Adding…';
    }

    try {
      await requestJSON(cartRoutes.add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
        }),
      });

      showToast(
        window.RGTheme?.isRTL
          ? '✓ تمت الإضافة للسلة'
          : '✓ Added to cart'
      );

      await openCartDrawer();
    } catch (error) {
      console.error('Unable to add product:', error);

      showToast(
        window.RGTheme?.isRTL
          ? 'حدث خطأ، حاول مجدداً'
          : 'Something went wrong'
      );
    } finally {
      if (button) {
        button.innerHTML = originalContent;
        button.disabled = false;
      }
    }
  };

  /* ── Cart Icon ─────────────────────────────── */

  const cartToggle =
    document.getElementById('cartToggle');

  if (cartToggle) {
    cartToggle.addEventListener('click', (event) => {
      event.preventDefault();
      openCartDrawer();
    });
  }

  /* ── Close Cart With Escape ────────────────── */

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeCartDrawer();
      closeMobileNav();
    }
  });

  /* ── Smooth Scrolling ──────────────────────── */

  document
    .querySelectorAll('a[href^="#"]')
    .forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');

        if (!href || href === '#') {
          return;
        }

        const target = document.querySelector(href);

        if (!target) {
          return;
        }

        event.preventDefault();

        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    });

  /* ── Mobile Navigation ─────────────────────── */

  const mobileMenuButton =
    document.getElementById('mobileMenuBtn');

  const mobileNav =
    document.getElementById('mobileNav');

  const mobileOverlay =
    document.getElementById('mobileNavOverlay');

  if (mobileMenuButton) {
    mobileMenuButton.addEventListener('click', () => {
      mobileNav?.classList.add('open');
      mobileOverlay?.classList.add('open');
      document.body.style.overflow = 'hidden';
    });
  }

  if (mobileOverlay) {
    mobileOverlay.addEventListener('click', () => {
      closeMobileNav();
    });
  }

  window.closeMobileNav = () => {
    mobileNav?.classList.remove('open');
    mobileOverlay?.classList.remove('open');
    document.body.style.overflow = '';
  };

  /* ── English Direction ─────────────────────── */

  if (
    window.RGTheme &&
    !window.RGTheme.isRTL
  ) {
    document.body.classList.add('lang-en');
    document.documentElement.setAttribute('dir', 'ltr');
  }

  /* ── Initial Cart Count ────────────────────── */

  updateCartCount();
});